import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import { ResponsiveBar } from "@nivo/bar";
import { Box } from "@mui/material";
import { makeStyles } from "@mui/styles";
import DateFunctions from "./DateFunctions";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";

const useStyles = makeStyles(() => ({
  box: {
    display: "flex",
    justifyContent: "center",
    height: 400,
  },
}));

export default function MainPlot(props) {
  const classes = useStyles();
  const [data, setData] = useState([]);

  useEffect(() => {
    let subUnsubscribes = [];

    const mainUnsubscribe = firebase
      .firestore()
      .collection("users")
      .onSnapshot((usersSnap) => {
        // Clean up previous subcollection listeners if user list updates
        subUnsubscribes.forEach((unsub) => unsub());
        subUnsubscribes = [];

        const normSum = DateFunctions.normSum(new Date());
        const userTotals = {};
        const userProfiles = [];

        usersSnap.forEach((userDoc) => {
          const userId = userDoc.id;
          const userData = userDoc.data();
          if (userData && userData.userinfo && userData.userinfo.name) {
            userProfiles.push({ id: userId, name: userData.userinfo.name });
            userTotals[userId] = { pushups: 0, situps: 0 };
          }
        });

        const updateChartData = () => {
          const newData = [];
          newData.push({
            name: "Norm",
            situps: normSum,
            pushups: normSum,
          });

          const limit = props.noLimit ? Number.MAX_SAFE_INTEGER : normSum;
          userProfiles.forEach(({ id, name }) => {
            const totals = userTotals[id];
            if (totals && (totals.pushups > 0 || totals.situps > 0)) {
              newData.push({
                name: name,
                pushups: Math.min(limit, totals.pushups),
                situps: Math.min(limit, totals.situps),
              });
            }
          });

          setData(newData);
        };

        // Attach subcollection listener for each user for current period
        userProfiles.forEach(({ id }) => {
          const unsub = firebase
            .firestore()
            .collection("users")
            .doc(id)
            .collection("data")
            .where(
              firebase.firestore.FieldPath.documentId(),
              ">=",
              DateFunctions.startDate
            )
            .onSnapshot((dataSnap) => {
              let situps = 0;
              let pushups = 0;

              dataSnap.forEach((doc) => {
                if (DateFunctions.inHalfYear(doc.id)) {
                  situps += doc.data().situps || 0;
                  pushups += doc.data().pushups || 0;
                }
              });

              userTotals[id] = { situps, pushups };
              updateChartData();
            });

          subUnsubscribes.push(unsub);
        });

        updateChartData();
      });

    return () => {
      subUnsubscribes.forEach((unsub) => unsub());
      mainUnsubscribe();
    };
  }, [props.noLimit]);

  return (
    <Box className={classes.box}>
      <ResponsiveBar
        groupMode="grouped"
        data={data}
        keys={["situps", "pushups"]}
        indexBy="name"
        margin={{ top: 50, right: 60, bottom: 50, left: 60 }}
        padding={0.3}
        colors={{ scheme: "set2" }}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
        }}
        enableLabel={false}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: "Sit-ups",
          legendPosition: "middle",
          legendOffset: -50,
        }}
        axisRight={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: "Push-ups",
          legendPosition: "middle",
          legendOffset: 50,
        }}
      />
    </Box>
  );
}

MainPlot.defaultProps = {
  noLimit: false,
};

MainPlot.propTypes = {
  noLimit: PropTypes.bool.isRequired,
};
